import React, { useRef, useEffect, useState } from "react";
import "./PlanSelection.css";
import { getUserId } from "../../utils/tokenUtils";
import { useLocation, useNavigate } from "react-router-dom";
import API_URL from "../../apiConfig";
import { SubscriptionPlanService } from "../../services/subsriptionPlans/subsriptionPlansService";
import {
  PlanViewModel,
  SubscriptionPlan,
} from "../../types/subsriptionPlans/subscriptionPlans";
import { HelenService } from "../../services/helen/helenService";

export default function PlanSelection() {
  const [helenBots, setHelenBots] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [plans, setPlans] = useState<PlanViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false); // New loading state
  const [userId] = useState(getUserId() || "");
  const [hasExistingSubscription, setHasExistingSubscription] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const plansContainerRef = useRef<HTMLDivElement>(null);

  const scrollPlans = (direction: "left" | "right") => {
    const container = plansContainerRef.current;
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await SubscriptionPlanService.getPlans();

        if (data && data.plans) {
          const mappedPlans = data.plans.map((plan: any) => ({
            id: plan.plan_id,
            name: plan.plan_name,
            tier: plan.pricing_tier,
            price: `$${plan.price}`,
            description: plan.plan_description,
            monthlyNote: plan.time_limit_applies,
            workingHours: plan.time_limit,
            workingTime: `${plan.start_time} - ${plan.end_time}`,
            users: plan.helen_licenses,
            brandVoice: true,
            chatAccess: true,
            seoAccess: true,
            browserExt: true,
            extras: [],
            popular: plan.plan_name === "Basic",
            dailyInteractions: plan.usage_limit,
          }));

          const sortedPlans = mappedPlans;
          setPlans(sortedPlans);
          setHasExistingSubscription(data.hasExistingSubscription);
          setLoading(false);
        }
      } catch (error) {
        setError("Failed to fetch plans.");
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        setIsProcessing(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const increaseBots = () => setHelenBots((v) => Math.min(v + 1, 99));
  const decreaseBots = () => setHelenBots((v) => Math.max(v - 1, 1));

  // Loading Spinner Component
  const LoadingSpinner = () => (
    <div className="loading-spinner">
      <div className="spinner"></div>
    </div>
  );

  if (loading)
    return (
      <div className="plan-selection-wrapper" style={{ color: "white" }}>
        Loading plans...
      </div>
    );
  if (error)
    return (
      <div className="plan-selection-wrapper" style={{ color: "red" }}>
        Error: {error}
      </div>
    );

  return (
    <div className="plan-selection-wrapper">
      <div className="plan-selection-container">
        <div className="plan-selection-header">
          <h2>
            Select a Plan to start your{" "}
            <span className="free-trial-text">FREE 7-day trial!</span>
          </h2>
        </div>

        <div className="bot-count-selector">
          <span className="bot-selector-label">
            {hasExistingSubscription
              ? "Select additional number of Helen Bots you would like"
              : "Select number of Helen Bots you would like"}
          </span>
          <div className="bot-count-controls">
            <button onClick={decreaseBots} className="bot-count-button">
              −
            </button>
            <span className="bot-count-display">
              {helenBots.toString().padStart(2, "0")}
            </span>
            <button onClick={increaseBots} className="bot-count-button">
              +
            </button>
          </div>
        </div>

        <div className="plans-wrapper">
          <button
            className="scroll-btn scroll-btn-left"
            onClick={() => scrollPlans("left")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>

          <div className="plans-container" ref={plansContainerRef}>
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`plan-card ${
                  selectedPlan === plan.id ? "selected" : ""
                } ${selectedPlan === null && plan.popular ? "popular" : ""}`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {selectedPlan === plan.id && (
                  <div className="popular-badge">Selected</div>
                )}
                {selectedPlan === null && plan.popular && (
                  <div className="popular-badge">Popular</div>
                )}

                <div className="plan-header">
                  <div className="plan-tier">{plan.tier}</div>
                  <div className="plan-pricing">
                    <div className="plan-price">
                      {plan.price}
                      <span className="price-period"> / per month per bot</span>
                    </div>
                    <span className="billing-note">Billed Monthly</span>
                  </div>
                </div>

                <div className="plan-divider"></div>

                <div className="daily-interactions-container">
                  <div className="daily-interactions-item">
                    <div className="feature-icon-wrapper">
                      <img src="/images/time.svg" />
                    </div>
                    <span>
                      Upto {plan.dailyInteractions} Product Copy Drafts Daily
                    </span>
                  </div>
                </div>

                <div className="daily-interactions-container">
                  <div className="daily-interactions-item">
                    <span>{plan.description}</span>
                  </div>
                </div>

                <div className="plan-divider"></div>

                {/* <div className="features-list">
                  <div className="feature-item">
                    <div className="feature-check">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                    </div>
                    <span>{plan.description}</span>
                  </div>
                </div> */}

                <button
                  className={`select-plan-button ${
                    selectedPlan === plan.id ? "selected" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPlan(plan.id);
                  }}
                >
                  {selectedPlan === plan.id ? "Selected" : "Select Plan"}
                </button>
              </div>
            ))}
          </div>

          <button
            className="scroll-btn scroll-btn-right"
            onClick={() => scrollPlans("right")}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z" />
            </svg>
          </button>
        </div>

        <div className="proceed-button-container">
          <button onClick={() => window.history.back()} className="back-button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Back
          </button>
          <button
            className={`proceed-button ${selectedPlan ? "enabled" : ""}`}
            disabled={selectedPlan === null || isProcessing}
            onClick={async () => {
              try {
                setIsProcessing(true); // Start loading

                const response = await HelenService.createSubscription(
                  userId,
                  selectedPlan,
                  helenBots,
                );

                if (response.status !== 200)
                  throw new Error(`HTTP error! status: ${response.status}`);

                const result = response.data;

                if (result.checkoutUrl) {
                  window.location.href = result.checkoutUrl;
                } else {
                  alert("Failed to create checkout session.");
                  setIsProcessing(false); // Stop loading on error
                }
              } catch (error) {
                alert(`Failed to create subscription: ${error}`);
                setIsProcessing(false); // Stop loading on error
              }
            }}
          >
            {isProcessing ? (
              <>
                <LoadingSpinner />
                Processing...
              </>
            ) : (
              <>
                Proceed
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M4 11v2h12l-5.5 5.5 1.42 1.42L19.84 12l-7.92-7.92L10.5 5.5 16 11H4z" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
